import dayjs from 'dayjs'
import type { MedplumClient } from '@medplum/core'
import type { LicenseDocument, LicenseRow } from '@behuman/core-contract'
import { states } from '@/utils/states'

// The card's row while it is being edited: the contract row with the required State select
// allowed to hold the empty string a fresh row starts with, plus the Supporting Document
// picked but not yet saved. The pending file is client-side only: the page's Save turns it
// into a Binary and sends its reference as `document`; the contract never sees the File.
export type LicenseRowValue = Omit<Partial<LicenseRow>, 'state'> & {
  state?: LicenseRow['state'] | ''
  pendingFile?: File | null
}

/**
 * State options for a single license row: every state except those already
 * chosen in sibling rows, but always keeping this row's own current selection so
 * it stays displayable/clearable.
 */
export function availableStateOptions<T extends { value: string }>(
  all: readonly T[],
  usedStates: readonly string[],
  currentValue: string | undefined,
): T[] {
  return all.filter((s) => s.value === currentValue || !usedStates.includes(s.value))
}

/**
 * The card's header: the state name once chosen, else "New license".
 */
export function licenseHeading(row: LicenseRowValue | undefined): string {
  return states.find((s) => s.value === row?.state)?.label ?? 'New license'
}

// How far ahead a license is badged as expiring.
const EXPIRY_WARNING_DAYS = 60

/**
 * The expiry badge beside a license heading: red "Expired" once the expiration date is
 * before today, amber "Expires in N days" from today (N = 0) through 60 days out, none
 * beyond that or without a date. N counts whole calendar days, so the badge never changes
 * within a day. Compared as calendar dates in the browser's zone: dayjs reads the stored
 * `YYYY-MM-DD` as a local date, where `new Date('YYYY-MM-DD')` would read it as UTC midnight
 * and show the day before west of Greenwich.
 */
export function expiryBadge(
  expiryDate: string | null | undefined,
): { color: 'red' | 'yellow'; label: string } | null {
  if (!expiryDate) return null
  const days = dayjs(expiryDate).startOf('day').diff(dayjs().startOf('day'), 'day')
  if (days < 0) return { color: 'red', label: 'Expired' }
  if (days > EXPIRY_WARNING_DAYS) return null
  return { color: 'yellow', label: `Expires in ${days} ${days === 1 ? 'day' : 'days'}` }
}

/**
 * A tab's first Save step for one picked file (a Supporting Document or the photo): it
 * becomes a Binary, written with the viewer's own Medplum client as every other console
 * upload is, and the form then sends its canonical Attachment (never the File). A Binary
 * whose credentials call then fails is left behind; the next Save creates a fresh one.
 */
export async function uploadPendingFile(
  medplum: MedplumClient,
  file: File,
): Promise<LicenseDocument> {
  const binary = await medplum.createBinary({
    data: file,
    contentType: file.type,
    filename: file.name,
  })
  return {
    url: `Binary/${binary.id}`,
    contentType: file.type,
    title: file.name,
    creation: new Date().toISOString(),
  }
}
